import { FC, useEffect, useState } from 'react';
import { systemApi, ApiError } from '@/services/api';

export interface SystemOption {
  id: string;
  name: string;
  interface_url: string;
  claude_api_url: string;
  openai_api_url: string;
  description?: string;
}

interface SystemSelectorProps {
  value: string;
  onChange: (systemId: string, systemInfo?: SystemOption) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

export const SystemSelector: FC<SystemSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
  placeholder = '选择系统',
  required = false
}) => {
  const [systems, setSystems] = useState<SystemOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSystems();
  }, []);

  const loadSystems = async () => {
    try {
      setLoading(true);
      setError('');
      const systemList = await systemApi.getSystemsForSelection();
      console.log('SystemSelector - 获取到的系统列表:', systemList);
      setSystems(systemList);
      
      // 如果当前没有选择且有系统可用，自动选择第一个
      if (systemList.length > 0 && !value) {
        onChange(systemList[0].id, systemList[0]);
        console.log('SystemSelector - 自动选择第一个系统:', systemList[0]);
      }
    } catch (err) {
      console.error('Failed to load systems:', err);
      setError(err instanceof ApiError ? err.message : '加载系统列表失败');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
        <span className="text-sm text-gray-500">加载系统...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm text-red-600 ${className}`}>
        <span className="mr-2">⚠️</span>
        {error}
        <button
          onClick={loadSystems}
          className="ml-2 text-primary-600 hover:text-primary-800 underline"
        >
          重试
        </button>
      </div>
    );
  }

  if (systems.length === 0) {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>
        暂无可用系统
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        const selectedSystem = systems.find(s => s.id === e.target.value);
        console.log('📡 SystemSelector - onChange 被触发:', {
          oldValue: value,
          newValue: e.target.value,
          selectedOption: selectedSystem
        });
        onChange(e.target.value, selectedSystem);
      }}
      disabled={disabled}
      required={required}
      className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm 
        focus:ring-2 focus:ring-primary-500 focus:border-transparent 
        disabled:bg-gray-100 disabled:cursor-not-allowed 
        ${className}`}
    >
      {!required && (
        <option value="">{placeholder}</option>
      )}
      {systems.map((system) => (
        <option key={system.id} value={system.id}>
          {system.name}
        </option>
      ))}
    </select>
  );
};

export default SystemSelector;